import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { fetchSimulateCandleBudgetPreview } from "../../../services/tradePolicyApi";
import type {
  SimulateCandleBudgetPreviewResponse,
  SimulateCandleBudgetSuggestedPeriod,
} from "../../../types/tradePolicy";
import type { AnalyticsWindowSnakeFields } from "../../../utils/analyticsWindowParams";
import {
  buildSuggestedSimulatePeriods,
  resolveBudgetWindowYmdBounds,
  SIMULATE_ANALYSIS_MAX_BARS,
  timeframeExceedsReportBarBudget,
} from "../../../utils/candleTimeframeBarBudget";

function periodKey(p: Pick<SimulateCandleBudgetSuggestedPeriod, "window_start" | "window_end">): string {
  return `${p.window_start}\0${p.window_end}`;
}

function ymdFromIsoOrYmd(raw: string): string {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(raw.trim());
  return m ? m[1]! : raw.trim();
}

export type SimulatorCandleBudgetGateProps = {
  candleTimeframe: string;
  windowFields: AnalyticsWindowSnakeFields;
  symbol?: string;
  brokerAccountCode?: string | null;
  reportId?: string | null;
  /** When true, never suggest coarsening — periods only. */
  candleTimeframeLocked?: boolean;
  /** Dual-TF: entry side (budget uses finest of candle/entry/management). */
  entryCandleTimeframe?: string | null;
  /** Dual-TF: management / Exit Placement replay TF. */
  managementCandleTimeframe?: string | null;
  activeLocalStart?: string | null;
  activeLocalEnd?: string | null;
  onSelectPeriod: (startYmd: string, endYmd: string) => void;
  onExceedsChange?: (exceeds: boolean) => void;
  /** False while a preview request is in flight (including debounce). */
  onPreviewSettledChange?: (settled: boolean) => void;
  /** When true, omit the inline banner (e.g. parent shows a footer alert instead). */
  hideBanner?: boolean;
  /** When true, omit period chips (parent only needs exceeds / settled callbacks). */
  hidePeriods?: boolean;
  /** Render nothing; still run budget preview callbacks. */
  silent?: boolean;
  /** Label above period chips. */
  periodsLabel?: string;
};

/**
 * Preflight for simulate OHLC bar budget. Prefers BE candle-budget-preview;
 * falls back to client period chunking when the preview fails or returns no periods.
 */
export function SimulatorCandleBudgetGate({
  candleTimeframe,
  windowFields,
  symbol,
  brokerAccountCode,
  reportId,
  candleTimeframeLocked = false,
  entryCandleTimeframe,
  managementCandleTimeframe,
  activeLocalStart,
  activeLocalEnd,
  onSelectPeriod,
  onExceedsChange,
  onPreviewSettledChange,
  hideBanner = false,
  hidePeriods = false,
  silent = false,
  periodsLabel = "Simulation periods",
}: SimulatorCandleBudgetGateProps): React.ReactElement | null {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<SimulateCandleBudgetPreviewResponse | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  const localEstimate = useMemo(
    () => timeframeExceedsReportBarBudget(candleTimeframe, windowFields),
    [candleTimeframe, windowFields]
  );

  const hasExplicitWindow =
    Boolean(windowFields.local_start_date && windowFields.local_end_date) ||
    Boolean(windowFields.start_date && windowFields.end_date);
  // BE requires an explicit local/ISO window for candle-budget-preview — do not
  // call with report_id + timeframe alone.
  const hasWindow = hasExplicitWindow;

  const clientFallbackPeriods = useMemo(() => {
    if (!localEstimate.exceeds) return [] as SimulateCandleBudgetSuggestedPeriod[];
    const bounds = resolveBudgetWindowYmdBounds(windowFields);
    if (!bounds) return [];
    return buildSuggestedSimulatePeriods({
      desiredStart: bounds.start,
      desiredEnd: bounds.end,
      candleTf: candleTimeframe,
      maxBars: SIMULATE_ANALYSIS_MAX_BARS,
      timeZone: windowFields.timezone,
    });
  }, [candleTimeframe, localEstimate.exceeds, windowFields]);

  const onExceedsChangeRef = React.useRef(onExceedsChange);
  onExceedsChangeRef.current = onExceedsChange;
  const onPreviewSettledChangeRef = React.useRef(onPreviewSettledChange);
  onPreviewSettledChangeRef.current = onPreviewSettledChange;

  const windowKey = useMemo(
    () =>
      [
        windowFields.local_start_date ?? "",
        windowFields.local_end_date ?? "",
        windowFields.start_date ?? "",
        windowFields.end_date ?? "",
        windowFields.timeframe ?? "",
        windowFields.timezone ?? "",
        candleTimeframe,
        entryCandleTimeframe ?? "",
        managementCandleTimeframe ?? "",
        symbol ?? "",
        brokerAccountCode ?? "",
        reportId ?? "",
      ].join("|"),
    [
      brokerAccountCode,
      candleTimeframe,
      entryCandleTimeframe,
      managementCandleTimeframe,
      reportId,
      symbol,
      windowFields.end_date,
      windowFields.local_end_date,
      windowFields.local_start_date,
      windowFields.start_date,
      windowFields.timeframe,
      windowFields.timezone,
    ]
  );

  useEffect(() => {
    if (!hasWindow) {
      setPreview(null);
      setPreviewFailed(false);
      setLoading(false);
      onExceedsChangeRef.current?.(false);
      onPreviewSettledChangeRef.current?.(true);
      return;
    }

    // Block Continue immediately — including the debounce window before fetch.
    setLoading(true);
    onPreviewSettledChangeRef.current?.(false);

    let cancelled = false;
    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          const sym = symbol?.trim() || "_";
          const resp = await fetchSimulateCandleBudgetPreview({
            symbol: sym,
            candle_timeframe: candleTimeframe,
            ...(entryCandleTimeframe?.trim()
              ? { entry_candle_timeframe: entryCandleTimeframe.trim() }
              : {}),
            ...(managementCandleTimeframe?.trim()
              ? { management_candle_timeframe: managementCandleTimeframe.trim() }
              : {}),
            ...(brokerAccountCode?.trim()
              ? { broker_account_code: brokerAccountCode.trim() }
              : {}),
            ...(reportId?.trim() ? { report_id: reportId.trim() } : {}),
            ...windowFields,
          });
          if (cancelled) return;
          setPreview(resp);
          setPreviewFailed(false);
          onExceedsChangeRef.current?.(Boolean(resp.exceeds_budget));
        } catch {
          if (cancelled) return;
          setPreview(null);
          setPreviewFailed(true);
          onExceedsChangeRef.current?.(Boolean(localEstimate.exceeds));
        } finally {
          if (!cancelled) {
            setLoading(false);
            onPreviewSettledChangeRef.current?.(true);
          }
        }
      })();
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
    // windowKey captures window field identity; windowFields / localEstimate read from closure.
  }, [windowKey, hasWindow, localEstimate.exceeds]);

  const exceeds = preview ? Boolean(preview.exceeds_budget) : Boolean(localEstimate.exceeds);
  const maxBars = preview?.max_bars ?? SIMULATE_ANALYSIS_MAX_BARS;
  const barCount = preview?.analysis_bar_count ?? localEstimate.barCount;
  const bePeriods =
    preview && preview.exceeds_budget && Array.isArray(preview.suggested_periods)
      ? preview.suggested_periods
      : [];
  const periods =
    bePeriods.length > 0 ? bePeriods : exceeds ? clientFallbackPeriods : [];

  const activeKey =
    activeLocalStart && activeLocalEnd
      ? `${activeLocalStart}\0${activeLocalEnd}`
      : periods.find((p) => p.is_current)
        ? periodKey(periods.find((p) => p.is_current)!)
        : null;

  const handleSelect = useCallback(
    (p: SimulateCandleBudgetSuggestedPeriod) => {
      onSelectPeriod(ymdFromIsoOrYmd(p.window_start), ymdFromIsoOrYmd(p.window_end));
    },
    [onSelectPeriod]
  );

  if (!hasWindow) return null;
  // Parent may mount this only for budget callbacks (footer alert owns the UX).
  if (silent) return null;
  if (!exceeds && !loading) return null;

  const showBanner = !hideBanner && (loading || exceeds);
  const showPeriods = !hidePeriods && exceeds && periods.length > 0;
  if (!showBanner && !showPeriods) return null;

  return (
    <div className="flex flex-col gap-2 sm:col-span-2" data-simulator-candle-budget-gate="">
      {showBanner ? (
        <Alert className="border-[color:var(--brand-warning-orange,#f59e0b)]/40">
          <AlertDescription className="font-[family-name:var(--font-primary)] text-sm text-gray-500 dark:text-sidebar-muted-foreground">
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Checking candle bar budget…
              </span>
            ) : exceeds ? (
              <>
                This window needs ~
                {typeof barCount === "number" ? barCount.toLocaleString() : "too many"} bars at{" "}
                <strong>{candleTimeframe}</strong>; the limit is {maxBars.toLocaleString()}.
                {candleTimeframeLocked
                  ? periods.length > 0
                    ? " Candle timeframe is locked by the policy — pick a simulation period below, then continue."
                    : " Candle timeframe is locked by the policy — choose a shorter date range (e.g. Last 30 days)."
                  : periods.length > 0
                    ? " Pick a shorter period below (or shorten the date range), then continue."
                    : " Shorten the date range, then continue."}
                {previewFailed && periods.length > 0 ? (
                  <span className="mt-1 block text-xs opacity-80">
                    Using an estimated period split (budget preview unavailable).
                  </span>
                ) : null}
              </>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {showPeriods ? (
        <div className="flex flex-col gap-1.5">
          <p className="m-0 text-xs font-[family-name:var(--font-primary)] text-gray-500 dark:text-sidebar-muted-foreground">
            {periodsLabel}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {periods.map((p) => {
              const key = periodKey(p);
              const selected = activeKey === key;
              const label =
                p.label?.trim() ||
                `${ymdFromIsoOrYmd(p.window_start)} → ${ymdFromIsoOrYmd(p.window_end)}`;
              return (
                <Button
                  key={key}
                  type="button"
                  size="sm"
                  variant={selected ? "gradient" : "outline"}
                  className={cn(
                    "h-8 font-[family-name:var(--font-primary)] text-xs",
                    selected && "pointer-events-none"
                  )}
                  onClick={() => handleSelect(p)}
                >
                  {label}
                  {typeof p.bar_count === "number" ? (
                    <span className="opacity-70"> · {p.bar_count.toLocaleString()} bars</span>
                  ) : null}
                </Button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
